import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";

import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { toast, localizeMessage } from '../utils/toast.js';
// AccountProfile's quick-action buttons link here as
// /activities?tab=posts|liked|saved, so the initial tab must be read from
// that query param instead of always defaulting to "all".
const getInitialTab = (search) => {
  const tab = new URLSearchParams(search).get("tab");
  return ["all", "posts", "liked", "saved"].includes(tab) ? tab : "all";
};

function Activities() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getInitialTab(location.search));
  const [activities, setActivities] = useState([]);
  const [posts, setPosts] = useState([]);
  const [likedPosts, setLikedPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchUserData();
  }, [activeTab]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      if (!token) {
        setError(t('activities.noToken'));
        setLoading(false);
        return;
      }

      let endpoint = '';
      switch (activeTab) {
        case 'all':
          endpoint = '/api/user/activities';
          break;
        case 'posts':
          endpoint = '/api/user/posts';
          break;
        case 'liked':
          endpoint = '/api/posts/liked';
          break;
        case 'saved':
          endpoint = '/api/posts/saved';
          break;
        default:
          endpoint = '/api/user/activities';
      }

      const response = await fetch(`${GREENWICH_BACKEND_URL}${endpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        switch (activeTab) {
          case 'all':
            setActivities(data.activities || []);
            break;
          case 'posts':
            setPosts(data.posts || []);
            break;
          case 'liked':
            setLikedPosts(data.posts || []);
            break;
          case 'saved':
            setSavedPosts(data.posts || []);
            break;
        }
      } else {
        throw new Error(data.error || t('activities.unknownApiError'));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm(t('activities.confirmDeletePost'))) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${GREENWICH_BACKEND_URL}/api/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {

        setPosts(prev => prev.filter(post => post._id !== postId));

        if (activeTab === 'all') {
          fetchUserData();
        }
      } else {
        const data = await response.json();
        toast(t('activities.errorDeletingPost', { message: data.error || t('activities.unknownApiError') }));
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast(t('activities.errorDeletingPost', { message: error.message }));
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return t('activities.justNow');
    } else if (diffInHours < 24) {
      return t('activities.hoursAgo', { n: diffInHours });
    } else if (diffInHours < 168) {
      return t('activities.daysAgo', { n: Math.floor(diffInHours / 24) });
    } else {
      return date.toLocaleDateString();
    }
  };

  const renderContent = () => {
    if (error) {
      return (
        <div className="error-state">
          <h3>{t('activities.errorLoadingTitle')}</h3>
          <p>{localizeMessage(error)}</p>
          <button onClick={fetchUserData} className="retry-btn">
            {t('activities.tryAgain')}
          </button>
        </div>
      );
    }

    if (loading) {
      return <div className="loading">{t('activities.loadingYour', { tab: activeTab })}</div>;
    }

    switch (activeTab) {
      case 'all':
        return (
          <div className="activities-list">
            {activities.length === 0 ? (
              <p className="empty-state">{t('activities.noActivitiesYet')}</p>
            ) : (
              activities.map(activity => (
                <div key={activity._id} className="activity-item">
                  <div className="activity-icon">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="activity-content">
                    <p className="activity-text">{getActivityText(activity)}</p>
                    <div className="activity-time-wrapper">
                      <span className="activity-time">
                        {formatTime(activity.createdAt)}
                      </span>
                      {activity.postId?.content && (
                        <span className="activity-preview">
                          "{activity.postId.content.substring(0, 50)}..."
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      case 'posts':
        return (
          <div className="posts-grid">
            {posts.length === 0 ? (
              <p className="empty-state">{t('activities.noPostsYetCreate')}</p>
            ) : (
              posts.map(post => (
                <div key={post._id} className="post-card">
                  <div className="post-header">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={post.userId?.profileImage || "/nkarner/default-avatar.jpg"}
                        alt={t('activities.profileAlt')}
                        className="post-avatar"
                      />
                      {post.userId?.isApproved === 'approved' && (
                        <Star
                          className="approved-star"
                          style={{
                            position: 'absolute',
                            bottom: '0',
                            right: '0',
                            width: '20px',
                            height: '20px',
                            color: '#FFD700',
                            fill: '#FFD700',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            padding: '2px'
                          }}
                        />
                      )}
                    </div>
                    <div className="post-author">
                      <span className="author-name">
                        {post.userId?.name || t('activities.you')} {post.userId?.surname || ''}
                      </span>
                      <span className="post-date">
                        {new Date(post.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <button
                      className="delete-post-btn"
                      onClick={() => handleDeletePost(post._id)}
                      title={t('activities.deletePost')}
                    >
                      <img src="/nkarner/trash.png" alt={t('activities.deleteAlt')} className="action-icon" />
                    </button>
                  </div>

                  {post.image && (
                    <img src={post.image} alt={t('activities.postAlt')} className="post-image" />
                  )}
                  <p className="post-content">{post.content}</p>

                  <div className="post-stats">
                    <div className="stat-item">
                      <img src="/nkarner/heart (1).png" alt={t('activities.likesAlt')} className="stat-icon" />
                      <span>{post.likes?.length || 0}</span>
                    </div>
                    <div className="stat-item">
                      <img src="/nkarner/chat (1).png" alt={t('activities.commentsAlt')} className="stat-icon" />
                      <span>{post.comments?.length || 0}</span>
                    </div>
                    <div className="stat-item">
                      <img src="/nkarner/bookmark (1).png" alt={t('activities.savesAlt')} className="stat-icon" />
                      <span>{post.saves?.length || 0}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      case 'liked':
        return (
          <div className="posts-grid">
            {likedPosts.length === 0 ? (
              <p className="empty-state">{t('activities.noLikedYet')}</p>
            ) : (
              likedPosts.map(post => (
                <div key={post._id} className="post-card">
                  <div className="post-header">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={post.userId?.profileImage || "/nkarner/default-avatar.jpg"}
                        alt={t('activities.profileAlt')}
                        className="post-avatar"
                      />
                      {post.userId?.isApproved === 'approved' && (
                        <Star
                          className="approved-star"
                          style={{
                            position: 'absolute',
                            bottom: '0',
                            right: '0',
                            width: '20px',
                            height: '20px',
                            color: '#FFD700',
                            fill: '#FFD700',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            padding: '2px'
                          }}
                        />
                      )}
                    </div>
                    <div className="post-author">
                      <span className="author-name">
                        {post.userId?.name || t('activities.unknown')} {post.userId?.surname || ''}
                      </span>
                      <span className="post-date">
                        {new Date(post.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {post.image && (
                    <img src={post.image} alt={t('activities.postAlt')} className="post-image" />
                  )}
                  <p className="post-content">{post.content}</p>

                  <div className="post-stats">
                    <div className="stat-item">
                      <img src="/nkarner/heart (1).png" alt={t('activities.likesAlt')} className="stat-icon" />
                      <span>{post.likes?.length || 0}</span>
                    </div>
                    <div className="stat-item">
                      <img src="/nkarner/chat (1).png" alt={t('activities.commentsAlt')} className="stat-icon" />
                      <span>{post.comments?.length || 0}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      case 'saved':
        return (
          <div className="posts-grid">
            {savedPosts.length === 0 ? (
              <p className="empty-state">{t('activities.noSavedYet')}</p>
            ) : (
              savedPosts.map(post => (
                <div key={post._id} className="post-card">
                  <div className="post-header">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={post.userId?.profileImage || "/nkarner/default-avatar.jpg"}
                        alt={t('activities.profileAlt')}
                        className="post-avatar"
                      />
                      {post.userId?.isApproved === 'approved' && (
                        <Star
                          className="approved-star"
                          style={{
                            position: 'absolute',
                            bottom: '0',
                            right: '0',
                            width: '20px',
                            height: '20px',
                            color: '#FFD700',
                            fill: '#FFD700',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            padding: '2px'
                          }}
                        />
                      )}
                    </div>
                    <div className="post-author">
                      <span className="author-name">
                        {post.userId?.name || t('activities.unknown')} {post.userId?.surname || ''}
                      </span>
                      <span className="post-date">
                        {new Date(post.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {post.image && (
                    <img src={post.image} alt={t('activities.postAlt')} className="post-image" />
                  )}
                  <p className="post-content">{post.content}</p>

                  <div className="post-stats">
                    <div className="stat-item">
                      <img src="/nkarner/heart (1).png" alt={t('activities.likesAlt')} className="stat-icon" />
                      <span>{post.likes?.length || 0}</span>
                    </div>
                    <div className="stat-item">
                      <img src="/nkarner/chat (1).png" alt={t('activities.commentsAlt')} className="stat-icon" />
                      <span>{post.comments?.length || 0}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'post': return '📝';
      case 'like': return '❤️';
      case 'save': return '💾';
      case 'comment': return '💬';
      default: return '🔔';
    }
  };

  const getActivityText = (activity) => {
    switch (activity.type) {
      case 'post': return t('activities.activityPost');
      case 'like': return t('activities.activityLike');
      case 'save': return t('activities.activitySave');
      case 'comment': return t('activities.activityComment');
      default: return activity.content || t('activities.newActivity');
    }
  };

  return (
    <div className="activities-page">
      <div className="page-header">
        <h1>{t('activities.title')}</h1>
        <p>{t('activities.subtitle')}</p>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            {t('activities.allActivities')}
          </button>
          <button
            className={`tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            {t('activities.myPosts')}
          </button>
          <button
            className={`tab ${activeTab === 'liked' ? 'active' : ''}`}
            onClick={() => setActiveTab('liked')}
          >
            {t('activities.likedPosts')}
          </button>
          <button
            className={`tab ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            {t('activities.savedPosts')}
          </button>
        </div>
      </div>

      <div className="activities-content">
        {renderContent()}
      </div>
    </div>
  );
}

export default Activities;
